import React, { useEffect } from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import useInitializeApp from '@utils/hooks/use-initialize-app';
import { Homepage } from '@app/pages/Homepage';
import { selectorRootState } from '@model/index';
import { ROUTES } from '@app/constants/navigation.constants';
import { ErrorScreenGeneric } from '@components/ErrorScreen/index';
import JobProgressSwitch from '@components/navigation/JobProgressSwitch/JobProgressSwitch';
import CandidateDetail from '@app/pages/CandidateDetail';
import { SuccessFee } from '@app/pages/SuccessFee';
import { DealRedirectManager } from '@components/navigation/DealRedirectManager/index';
import { SUCCESS_FEE_SEGMENT } from '@app/constants/navigation.constants';
import Meta from '@components/Meta/Meta';
import { useTranslation } from 'react-i18next';
import {
  jobStoreSelector,
  selectorJobIsReverseMarketing,
} from '@model/Job/Job.selectors';
import { contractStoreStateSelector } from '@model/Contract/Contract.selectors';
import { LoginSwitch } from '@components/navigation/LoginSwitch/index';

const MainSwitch = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const rootState = useStoreState(selectorRootState);
  const job = useStoreState(jobStoreSelector);
  const contract = useStoreState(contractStoreStateSelector);

  const entity = job || contract;
  const isJobReverseMarketing = useStoreState(selectorJobIsReverseMarketing);

  const step = isJobReverseMarketing
    ? t(`general.document_title_reverse_marketing.${rootState.currentRoute}`)
    : t(`general.document_title.${rootState.currentRoute}`);

  useInitializeApp();

  const basePath = (__ENVIRONMENT_CONFIG__?.PUBLIC_URL || '').replace(
    /\/$/,
    '',
  );
  const loginPath = basePath ? `${basePath}/login` : '/login';
  const pathFirstSegment = pathname.split('/').filter(Boolean)[0];
  const isNumericDealPath = !isNaN(pathFirstSegment);

  const isLoginPage =
    pathname === loginPath || pathname.startsWith(`${loginPath}/`);

  useEffect(() => {
    if (rootState.authenticationError && !isLoginPage) {
      navigate(ROUTES.LOGIN);
    }
  }, [rootState.authenticationError, isLoginPage, navigate]);

  if (rootState.appError) {
    return <ErrorScreenGeneric />;
  }

  if (isNumericDealPath) {
    return (
      <Routes>
        <Route
          path="/:dealId/:segment?/:applicationId?"
          element={<DealRedirectManager />}
        />
      </Routes>
    );
  }

  if (['jobs', 'contracts'].includes(pathFirstSegment)) {
    return (
      <>
        {rootState.currentRoute && entity && (
          <Meta
            title={t('common.meta.titleInternalPages', {
              step,
              entityTitle: entity?.title,
            })}
          />
        )}

        <Routes>
          <Route path="/jobs/:jobId/candidates/:candidateId/*">
            <Route path="" element={<CandidateDetail />} />
            <Route path="success-fee" element={<SuccessFee />} />
          </Route>

          <Route
            path="/:entityType/:entityId/*"
            element={<JobProgressSwitch />}
          />
        </Routes>
      </>
    );
  }

  return (
    <>
      {rootState.currentRoute && entity && (
        <Meta
          title={t('common.meta.titleInternalPages', {
            step,
            entityTitle: entity?.title,
          })}
        />
      )}

      <Routes>
        <Route path="/login/*" element={<LoginSwitch />} />
        <Route path="/customers/:customerId/*" element={<Homepage />} />
        <Route path="/*" element={<Homepage />} />
      </Routes>
    </>
  );
};

export default MainSwitch;
